import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    useReactTable,
    OnChangeFn,
    SortingState,
} from '@tanstack/react-table';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FiEye } from 'react-icons/fi';
import moment from 'moment';
import { formatCurrency } from '~/common/form/Inputs';
import { formatPaymentSource } from '~/common/paymentSource';
import { buildStripePaymentUrl } from '~/common/stripeUrl';
import { Pagination } from '~/components/Pagination';
import { CampaignRefundRow } from '~/services/campaign';

const columnHelper = createColumnHelper<CampaignRefundRow>();

export const REFUNDS_PAGE_SIZE = 20;

type Props = {
    rows: CampaignRefundRow[];
    count: number;
    page: number;
    setPage: (page: number) => void;
    isLoading: boolean;
    isError: boolean;
    /** Line above the table — row count on the left, anything else on the right. */
    summary: React.ReactNode;
    emptyMessage: string;
    errorMessage: string;
    /** Off for a single campaign's list, where every row is the same campaign. */
    showCampaign?: boolean;
    /**
     * Off where the caller only ever asks for succeeded refunds, so a status
     * column would read "succeeded" on every row.
     */
    showStatus?: boolean;
    /**
     * Turns the RE + MIP cell into a mark-complete / reopen control. Optional
     * because the caller owns the request and the row update — a screen that
     * can't patch its own rows leaves the flag read-only.
     */
    onToggleCompletion?: (
        refundId: number,
        next: 'complete' | 'pending'
    ) => void;
    /** Refund with a toggle in flight, so only that row's button locks. */
    savingRefundId?: number | null;
    /**
     * Adds a Detail column that opens the donation drawer on the row's
     * donation. Keyed by refund id, not donation id, because a full refund
     * deletes the donation row and the record has to be rebuilt from the refund
     * itself. Optional for the same reason as the toggle: the drawer and the
     * fetch that feeds it belong to the caller.
     */
    onOpenDonation?: (refundId: number) => void;
    /** Refund whose donation is loading, so only that row's icon spins. */
    openingRefundId?: number | null;
    /**
     * Sort state, owned by the caller because the sort is the server's — the
     * column has to reach the fetch, and the state has to outlive a page change
     * (HHLTB-2654).
     */
    sorting: SortingState;
    onSortingChange: OnChangeFn<SortingState>;
};

/**
 * Columns the refunds endpoints can order the whole result set by — refund
 * fields, in short. Everything else on a row (donor name, campaign, payment
 * source, what's still contributed) is resolved per page from the charge or the
 * loc row, so there is no column to sort on and the header stays inert rather
 * than shuffling twenty rows and calling it a sort.
 */
const SORTABLE_COLUMNS = new Set([
    'refunded_at',
    'total_refunded',
    'status',
    'completion_status',
]);

const stripeStatusStyles: Record<string, string> = {
    succeeded: 'bg-success text-white',
    pending: 'bg-warning text-black',
    failed: 'bg-red text-white',
};

const stripeStatusLabels: Record<string, string> = {
    succeeded: 'Processed',
    pending: 'Pending',
    failed: 'Failed',
};

/**
 * The refunds table, shared by the campaign donor list and the One Time
 * Contributions "Refunds" view.
 *
 * Presentational only — the caller owns fetching, filters and paging, because
 * the two screens read different endpoints (one campaign vs. all of them).
 */
const RefundsTable: React.FC<Props> = ({
    rows,
    count,
    page,
    setPage,
    isLoading,
    isError,
    summary,
    emptyMessage,
    errorMessage,
    showCampaign = false,
    showStatus = false,
    onToggleCompletion,
    savingRefundId = null,
    onOpenDonation,
    openingRefundId = null,
    sorting,
    onSortingChange,
}) => {
    const pages = useMemo(() => {
        if (!count) return 1;
        return Math.ceil(count / REFUNDS_PAGE_SIZE);
    }, [count]);

    const columns = useMemo(() => {
        const base = [
            columnHelper.accessor('name', {
                header: () => 'Donor',
                cell: (props) => {
                    const name = props.getValue();
                    const email = props.row.original.email;
                    return (
                        <div className='flex flex-col gap-0.5'>
                            <span>{name || 'Name not recorded'}</span>
                            {email && (
                                <a
                                    className='break-all text-[12px]/[16px] font-bold text-primary'
                                    href={`mailto:${email}`}
                                >
                                    {email}
                                </a>
                            )}
                        </div>
                    );
                },
            }),
            columnHelper.accessor('refunded_at', {
                header: () => 'Refunded',
                cell: (props) => {
                    const value = props.getValue();
                    if (!value) return '';
                    const by = props.row.original.refunded_by;
                    return (
                        <div className='flex flex-col'>
                            <span className='whitespace-nowrap'>
                                {moment.utc(value).local().format('MM/DD/YYYY')}
                            </span>
                            {by && (
                                <span className='text-[12px]/[16px] text-[#949599]'>
                                    {by}
                                </span>
                            )}
                        </div>
                    );
                },
            }),
        ];

        const campaignColumn = columnHelper.accessor('campaign_name', {
            header: () => 'Campaign',
            cell: (props) => {
                const name = props.getValue();
                const id = props.row.original.campaign_id;
                const ntaf = props.row.original.ntaf_id;
                const displayName =
                    ntaf === 'NTAF' ? 'General Operating Fund' : name;
                return (
                    <div className='flex flex-col'>
                        {displayName ? (
                            id ? (
                                <Link
                                    to={`/campaigns/${id}`}
                                    className='font-bold text-primary'
                                >
                                    {displayName}
                                </Link>
                            ) : (
                                <span>{displayName}</span>
                            )
                        ) : null}
                        {ntaf && (
                            <span className='text-[12px]/[16px] text-[#949599]'>
                                {ntaf}
                            </span>
                        )}
                    </div>
                );
            },
        });

        const amountColumns = [
            columnHelper.accessor('total_refunded', {
                header: () => 'Refunded Amount',
                cell: (props) => {
                    const row = props.row.original;
                    const settled = row.status === 'succeeded';
                    return (
                        <div className='flex flex-col items-start'>
                            <span className='whitespace-nowrap'>
                                {formatCurrency(Number(props.getValue()))}
                            </span>
                            <span
                                className={`mt-0.5 max-w-full rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase leading-tight tracking-wide ${
                                    row.is_full_refund
                                        ? 'bg-red text-white'
                                        : 'bg-gray-200 text-gray-600'
                                }`}
                                title={
                                    !settled
                                        ? `This refund never settled, so the full ${formatCurrency(Number(row.original_amount))} donation is still on the books.`
                                        : row.is_full_refund
                                          ? 'Refunded in full — the donation record was removed from the contributions list'
                                          : `Partial refund. ${formatCurrency(Number(row.remaining_amount))} of the original ${formatCurrency(Number(row.original_amount))} is still with the campaign.`
                                }
                            >
                                {!settled
                                    ? `Requested of ${formatCurrency(Number(row.original_amount))}`
                                    : row.is_full_refund
                                      ? 'Fully refunded'
                                      : `Partial of ${formatCurrency(Number(row.original_amount))}`}
                            </span>
                        </div>
                    );
                },
            }),
            columnHelper.accessor('remaining_amount', {
                header: () => 'Still Contributed',
                cell: (props) => (
                    <span className='whitespace-nowrap'>
                        {formatCurrency(Number(props.getValue()))}
                    </span>
                ),
            }),
            columnHelper.accessor('payment_method', {
                header: () => 'Payment Source',
                cell: (props) => {
                    const label = formatPaymentSource(props.getValue());
                    const chargeId = props.row.original.stripe_charge_id;
                    if (!chargeId) return label;
                    return (
                        <a
                            href={buildStripePaymentUrl(chargeId) || '#'}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='font-bold text-primary'
                        >
                            {label}
                        </a>
                    );
                },
            }),
        ];

        const statusColumn = columnHelper.accessor('status', {
            header: () => 'Refund Status',
            cell: (props) => {
                const value = props.getValue();
                const reason = props.row.original.failure_reason;
                return (
                    <div className='flex flex-col items-start'>
                        <span
                            className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ${
                                stripeStatusStyles[value] ??
                                'bg-gray-200 text-gray-600'
                            }`}
                            title={
                                value === 'pending'
                                    ? 'Started in the portal but never confirmed by Stripe — verify in Stripe before re-issuing'
                                    : value === 'failed'
                                      ? 'Stripe rejected this refund; no money moved'
                                      : 'Stripe accepted this refund'
                            }
                        >
                            {stripeStatusLabels[value] ?? value}
                        </span>
                        {reason && (
                            <span
                                className='mt-0.5 max-w-[220px] text-[11px] text-[#949599]'
                                title={reason}
                            >
                                {reason}
                            </span>
                        )}
                    </div>
                );
            },
        });

        const completionColumn = columnHelper.accessor('completion_status', {
            header: () => 'RE + MIP',
            cell: (props) => {
                const row = props.row.original;
                const value = props.getValue();
                const isComplete = value === 'complete';

                // A failed refund never moved money, so there is nothing to
                // enter into RE/MIP and nothing to toggle.
                if (onToggleCompletion && row.status === 'failed') {
                    return (
                        <span
                            className='text-[#949599]'
                            title='Refund failed — no RE/MIP entry to make'
                        >
                            —
                        </span>
                    );
                }

                // From the audit log, so a name survives a reopen — which nulls
                // the row's own completed_by. Falls back to the completed_at
                // stamp for refunds toggled before tracking started.
                const changedAt =
                    row.completion_changed_at || row.completed_at;
                const lastChange = row.completion_changed_by
                    ? [
                          isComplete ? 'Marked by' : 'Reopened by',
                          row.completion_changed_by,
                          changedAt
                              ? `· ${moment.utc(changedAt).local().format('MM/DD/YYYY')}`
                              : '',
                      ]
                          .filter(Boolean)
                          .join(' ')
                    : '';
                const saving = savingRefundId === row.refund_id;

                return (
                    <div className='flex flex-col items-start gap-0.5'>
                        <span
                            className={`rounded-full px-2 py-0.5 text-[11px] font-medium capitalize ${
                                isComplete
                                    ? 'bg-success text-white'
                                    : 'bg-warning text-black'
                            }`}
                            title='Whether finance has hand-entered this refund into Raiser’s Edge and MIP'
                        >
                            {value || 'pending'}
                        </span>
                        {onToggleCompletion ? (
                            <>
                                {lastChange && (
                                    <span className='whitespace-nowrap text-[11px] text-[#949599]'>
                                        {lastChange}
                                    </span>
                                )}
                                <button
                                    type='button'
                                    onClick={() =>
                                        onToggleCompletion(
                                            row.refund_id,
                                            isComplete ? 'pending' : 'complete'
                                        )
                                    }
                                    disabled={saving}
                                    className={
                                        isComplete
                                            ? 'text-[11px] text-[#949599] underline transition-colors hover:text-primary disabled:opacity-50'
                                            : 'mt-0.5 inline-flex items-center rounded border border-stroke px-2 py-1 text-[11px] font-medium text-body transition-colors hover:border-primary hover:text-primary disabled:opacity-50'
                                    }
                                >
                                    {saving
                                        ? 'Saving…'
                                        : isComplete
                                          ? 'Reopen'
                                          : 'Mark Complete'}
                                </button>
                            </>
                        ) : (
                            isComplete &&
                            row.completed_at && (
                                <span className='text-[11px] text-[#949599]'>
                                    {moment
                                        .utc(row.completed_at)
                                        .local()
                                        .format('MM/DD/YYYY')}
                                </span>
                            )
                        )}
                    </div>
                );
            },
        });

        const detailColumn = columnHelper.display({
            id: 'detail',
            header: () => 'Detail',
            cell: (props) => {
                const row = props.row.original;
                const opening = openingRefundId === row.refund_id;
                // Offered on every row, including full refunds: the donation
                // row is gone but the charge is still on record in Stripe, so
                // the drawer rebuilds what it can and reads as history.
                return (
                    <button
                        type='button'
                        onClick={() => onOpenDonation?.(row.refund_id)}
                        disabled={opening}
                        className='text-primary transition-opacity hover:text-opacity-80 disabled:opacity-50'
                        title={
                            row.donation_id
                                ? 'View donation detail'
                                : 'View donation detail — refunded in full, so the donation record is gone and this is rebuilt from Stripe'
                        }
                    >
                        {opening ? (
                            <span className='block h-[18px] w-[18px] animate-spin rounded-full border-2 border-primary border-t-transparent' />
                        ) : (
                            <FiEye size={20} />
                        )}
                    </button>
                );
            },
        });

        return [
            ...base,
            ...(showCampaign ? [campaignColumn] : []),
            ...amountColumns,
            ...(showStatus ? [statusColumn] : []),
            completionColumn,
            ...(onOpenDonation ? [detailColumn] : []),
        ];
        // The action cells close over the in-flight ids, so the columns have to
        // be rebuilt while a toggle is saving or a drawer is loading.
    }, [
        showCampaign,
        showStatus,
        onToggleCompletion,
        savingRefundId,
        onOpenDonation,
        openingRefundId,
    ]);

    const table = useReactTable({
        data: rows,
        columns,
        state: { sorting },
        onSortingChange,
        getCoreRowModel: getCoreRowModel(),
        // No getSortedRowModel: the server orders the whole result set, and
        // re-sorting the fetched page on top of that only shuffles twenty rows.
        manualSorting: true,
    });

    return (
        <>
            <div
                className='flex items-center justify-between border-t border-stroke px-8 py-1'
                style={{ fontSize: '12px' }}
            >
                {summary}
            </div>

            <div className='overflow-x-auto'>
                <table className='datatable-table datatable-one w-full table-auto border-collapse break-words px-4 md:px-8 2xl:table-fixed'>
                    <thead className='border-separate px-4'>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr
                                className='border-t border-stroke bg-whiten'
                                style={{ fontSize: '14px' }}
                                key={headerGroup.id}
                            >
                                {headerGroup.headers.map((header) => {
                                    const canSort = SORTABLE_COLUMNS.has(
                                        header.column.id
                                    );
                                    return (
                                        <th
                                            style={{
                                                paddingTop: '16px',
                                                paddingBottom: '16px',
                                            }}
                                            key={header.id}
                                        >
                                            <div
                                                className={`flex select-none items-center gap-1 ${
                                                    canSort
                                                        ? 'cursor-pointer'
                                                        : ''
                                                }`}
                                                onClick={
                                                    canSort
                                                        ? header.column.getToggleSortingHandler()
                                                        : undefined
                                                }
                                            >
                                                {header.isPlaceholder
                                                    ? null
                                                    : flexRender(
                                                          header.column
                                                              .columnDef.header,
                                                          header.getContext()
                                                      )}
                                                {{
                                                    asc: ' ▲',
                                                    desc: ' ▼',
                                                }[
                                                    header.column.getIsSorted() as string
                                                ] ?? ''}
                                            </div>
                                        </th>
                                    );
                                })}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {isLoading &&
                            Array.from({ length: 3 }).map((_, index) => (
                                <tr key={index}>
                                    {columns.map((_column, colIndex) => (
                                        <td
                                            key={colIndex}
                                            className='border-b border-stroke px-4 py-2 dark:border-strokedark'
                                        >
                                            <div className='max-w-sm animate-pulse'>
                                                <div className='h-2 max-w-[360px] rounded-full bg-gray-4 dark:bg-gray-5'></div>
                                            </div>
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        {isError && (
                            <tr>
                                <td
                                    colSpan={columns.length}
                                    className='px-8 py-6 text-center text-[13px] text-[#949599]'
                                >
                                    {errorMessage}
                                </td>
                            </tr>
                        )}
                        {!isLoading && !isError && rows.length === 0 && (
                            <tr>
                                <td
                                    colSpan={columns.length}
                                    className='px-8 py-6 text-center text-[13px] text-[#949599]'
                                >
                                    {emptyMessage}
                                </td>
                            </tr>
                        )}
                        {!isLoading &&
                            table.getRowModel().rows.map((row) => (
                                <tr
                                    className='border-t border-stroke'
                                    key={row.id}
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <td
                                            style={{
                                                paddingTop: '16px',
                                                paddingBottom: '16px',
                                            }}
                                            key={cell.id}
                                        >
                                            {flexRender(
                                                cell.column.columnDef.cell,
                                                cell.getContext()
                                            )}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>

            <Pagination
                page={page}
                setPage={setPage}
                pages={pages}
                totalCount={count}
            />
        </>
    );
};

export default RefundsTable;
